import { CHAIN_NAME, NATIVE_TOKEN_ADDRESS } from 'config/tokens'
import { useSubaccount } from 'context/SubaccountContext/SubaccountContext'
import { BigNumber, Contract } from 'utils/ethersImport'
import { callContract, showCallContractToast } from 'rfx/lib/contracts'
import { TxError, getErrorMessage } from 'rfx/lib/contracts/transactionErrors'
import { helperToast } from 'rfx/lib/helperToast'
import { usePendingTxns } from 'rfx/lib/usePendingTxns'
import { getProvider } from 'rfx/lib/rpc'
import React, { useMemo } from 'react'
import { Account, Address, Hex, stringify } from 'viem'
import { eip712WalletActions } from 'viem/zksync'
import { useAccount, useWalletClient } from 'wagmi'
import commonStore from '@store/commonStore'
import useWallet from 'rfx/lib/wallets/useWallet'
import { HYPEREVM, SONIC } from 'config/chains'

interface SwapCall {
  address: string
  calldata: Hex
  value?: bigint
}

export interface PendingTransaction {
  hash: string
  message: string | undefined
  messageDetails?: React.ReactNode
}

export interface ZyfiResponse {
  txData: TxData
  gasLimit: string
  gasPrice: string
  tokenAddress: string
  tokenPrice: string
  feeTokenAmount: string
  feeTokendecimals: string
  feeUSD: string
  estimatedFinalFeeUSD: string
  estimatedFinalFeeTokenAmount: string
  markup: string
  expirationTime: string
  expiresIn: string
  sponsorshipRatio: number
  warnings: string[]
}

export interface TxData {
  chainId: number
  from: Address
  to: Address
  data: Hex
  value: Hex
  customData: CustomData
  maxFeePerGas: string
  gasLimit: number
}

export interface CustomData {
  paymasterParams: PaymasterParams
  gasPerPubdata: number
}

export interface PaymasterParams {
  paymaster: string
  paymasterInput: string
}

export interface MessageOptions {
  detailsMsg?: React.ReactNode
  sentMsg?: string
  successMsg?: string
  hideSentMsg?: boolean
  hideSuccessMsg?: boolean
  showPreliminaryMsg?: boolean
  failMsg?: string
}

export interface SendPaymasterTransactionArgs {
  call: SwapCall & {
    gas?: string | bigint | undefined
    value?: bigint
  }
  account: string
  subaccount?: ReturnType<typeof useSubaccount>
  messageOpts?: MessageOptions
  setPendingTxns?: (txns: PendingTransaction[]) => void
  router: Contract
  payload: Array<string | BigNumber | string[]>
  method: string
}
export type SendPaymasterTransactionFn = (
  args: SendPaymasterTransactionArgs,
) => Promise<string>

/**
 * Zyfi Paymaster for the zkSync chain
 */

export const usePaymaster = () => {
  const [pendingTxns] = usePendingTxns()
  const { chain } = useAccount()
  const { chainId } = useWallet()
  const { data: walletClient } = useWalletClient()
  const setRecentTxns = commonStore((store) => store.setRecentTxns)

  const { txnType } = commonStore((store) => store)

  /**
   * Check if the Paymaster for zkSync is available
   */
  const isPaymasterAvailable = useMemo(() => {
    return chain?.id === chainId
  }, [chain])

  /**
   * Check if a paymaster token is selected.
   * Default is the native token to pay gas
   */
  const isPaymasterTokenActive = NATIVE_TOKEN_ADDRESS

  const sendPaymasterTransaction: SendPaymasterTransactionFn =
    React.useCallback(
      async ({
        call,
        account,
        messageOpts: opts = {},
        setPendingTxns,
        subaccount,
        router,
        payload,
        method,
      }) => {
        if (!account) {
          throw new Error(
            'An active wallet connection is required to send paymaster transaction',
          )
        }
        if (!isPaymasterAvailable || !isPaymasterTokenActive) {
          throw new Error('Paymaster is not available or active.')
        }
        const provider = getProvider(undefined, chainId) as any

        const nonce = await provider.getTransactionCount(
          subaccount?.account.address || account,
          'pending',
        )
        if (chainId === SONIC || chainId === HYPEREVM || txnType === 'normal') {
          const tx = await callContract(chainId, router, method, payload, {
            value: BigNumber.from(call?.value || 0),
            hideSentMsg: true,
            hideSuccessMsg: true,
            nonce,
            setPendingTxns: setPendingTxns,
            setRecentTxns,
            ...opts,
          })
          // eslint-disable-next-line no-console
          console.log({ tx })
          return tx
        }

        const response = await fetch('/api/paymaster', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },

          body: stringify({
            call,
            account: subaccount?.address || account,
            gasTokenAddress: '0x1d17CBcF0D6D143135aE902365D2E5e2A16538D4',
            chainID: chainId,
          }),
        })

        const txResponse: ZyfiResponse = await response.json()
        const rawTx = txResponse.txData

        if (
          txResponse.sponsorshipRatio < 100 ||
          txResponse?.warnings?.length > 0 ||
          !response.ok
        ) {
          const tx = await callContract(chainId, router, method, payload, {
            value: BigNumber.from(call?.value || 0),
            hideSentMsg: true,
            hideSuccessMsg: true,
            nonce,

            setPendingTxns: setPendingTxns,
            setRecentTxns,
            ...opts,
          })

          return tx
        } else {
          const txPayload = {
            account: (subaccount?.account || account) as
              | `0x${string}`
              | Account,
            to: rawTx.to,
            value: BigInt(rawTx.value),
            chain: chain,
            gas: BigInt(rawTx.gasLimit),
            gasPerPubdata: BigInt(rawTx.customData.gasPerPubdata),
            maxFeePerGas: BigInt(rawTx.maxFeePerGas),
            maxPriorityFeePerGas: BigInt(0),
            data: rawTx.data,
            paymaster: rawTx.customData.paymasterParams.paymaster,
            paymasterInput: rawTx.customData.paymasterParams.paymasterInput,
            nonce,
          }

          const balance = await provider.getBalance(
            subaccount?.account.address || account,
          )

          if (balance.lt(BigNumber.from(rawTx.value))) {
            helperToast.error(
              `You do not have enough ETH in your account to pay for transaction fees on ${CHAIN_NAME[chainId]} Mainnet network. Buy ETH or deposit from another account.`,
              {
                autoClose: 5000,
              },
            )
            throw new Error(
              `User do not have enough ETH in their account to pay for transaction fees on ${CHAIN_NAME[chainId]} Mainnet network.`,
            )
          }

          if (!walletClient) {
            throw new Error('Failed to execute paymaster transaction')
          }

          const client = walletClient.extend(eip712WalletActions())

          try {
            const txReq = await client.prepareTransactionRequest(txPayload)
            const signature = await client.signTransaction(txReq)
            const hash = await client.sendRawTransaction({
              serializedTransaction: signature,
            })

            if (!opts.hideSentMsg) {
              showCallContractToast({
                chainId: chain?.id || chainId,
                sentMsg: opts.sentMsg || `Transaction sent.`,
                detailsMsg: opts.detailsMsg || '',
                hash,
              })
            }

            if (setPendingTxns) {
              const message = opts.hideSuccessMsg
                ? undefined
                : opts.successMsg || `Transaction completed!`
              const pendingTxn = {
                hash: hash,
                message,
                messageDetails: opts.detailsMsg,
              }
              setPendingTxns([...pendingTxns, pendingTxn])
            }
            return hash
          } catch (e) {
            const { failMsg, autoCloseToast } = getErrorMessage(
              chain?.id || 0,
              e as TxError,
              opts?.failMsg,
            )

            helperToast.error(failMsg, { autoClose: autoCloseToast })
            throw e
          }
        }
      },
      [
        chain,
        isPaymasterAvailable,
        isPaymasterTokenActive,
        pendingTxns,
        walletClient,
        txnType,
      ],
    )

  return {
    isPaymasterAvailable,
    isPaymasterTokenActive,
    sendPaymasterTransaction,
  }
}
